import { useState } from "react";
import {
  useGetNewsEventsQuery,
  usePollNewsNowMutation,
  useDismissNewsEventMutation,
  useSellAffectedForNewsMutation,
  type NewsEvent,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StalenessBadge } from "@/components/domain/StalenessBadge";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtTime, fmtRelative } from "@/lib/format";
import { toast } from "sonner";
import { ExternalLink, RefreshCw, ShieldAlert, X } from "lucide-react";

/** News watcher dashboard — Binance announcements + general crypto RSS
 *  events that touch any symbol we trade or hold. High/critical events
 *  may auto-pause new entries (per news_severity_auto_pause setting).
 *  Operator can dismiss benign items or click "Sell affected" to close
 *  every open position whose base asset is in the event's symbol list. */
export default function News() {
  const [filter, setFilter] = useState<"all" | "open">("open");
  const q = useGetNewsEventsQuery(
    { status: filter === "open" ? "open" : undefined } as never,
    { pollingInterval: 60000 },
  );
  const [pollNow, { isLoading: polling }] = usePollNewsNowMutation();
  const [dismiss] = useDismissNewsEventMutation();
  const [sellAffected, { isLoading: selling }] = useSellAffectedForNewsMutation();

  const events = q.data ?? [];
  // Most-recent event ts — shows whether the watcher is actually polling
  const latestEventTs = events.reduce<string | null>(
    (m: string | null, e: any) => {
      const t = e.detected_at || e.ts;
      return t && (!m || t > m) ? t : m;
    }, null);

  const onPoll = async () => {
    try {
      const r = await pollNow().unwrap();
      toast.success(`Watcher ran — ${(r as any).new_events ?? 0} new event(s)`);
    } catch (e: any) {
      toast.error(e?.data?.detail ?? "Poll failed");
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 flex-wrap">
              News &amp; risk events
              <StalenessBadge ts={latestEventTs} warnHours={1} staleHours={4} veryStaleHours={24} label="last event" />
              <span className="text-xs text-muted ml-2 font-normal">
                Binance delistings / monitoring · general crypto RSS for held assets
              </span>
            </CardTitle>
            <div className="flex gap-2 pt-2">
              {(["open", "all"] as const).map((k) => (
                <button key={k}
                        onClick={() => setFilter(k)}
                        className={"text-xs px-3 py-1 rounded-md border " +
                          (filter === k ? "bg-bg2 text-fg border-accent"
                                        : "border-border text-muted hover:text-fg")}>
                  {k}
                </button>
              ))}
            </div>
          </div>
          <Button size="sm" variant="primary" disabled={polling} onClick={onPoll}>
            <RefreshCw className="w-3.5 h-3.5" /> Poll now
          </Button>
        </CardHeader>
        <CardContent>
          {q.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : events.length === 0 ? (
            <p className="text-sm text-muted">
              No {filter === "open" ? "open" : ""} news events. The watcher polls
              every 15 min by default — click "Poll now" to force a refresh.
            </p>
          ) : (
            <div className="space-y-3">
              {events.map((e) => (
                <EventCard
                  key={e.id} event={e}
                  selling={selling}
                  onDismiss={async () => {
                    try { await dismiss(e.id).unwrap(); toast.success("Dismissed"); }
                    catch (err: any) { toast.error(err?.data?.detail ?? "Failed"); }
                  }}
                  onSell={async () => {
                    const syms = symbolsOf(e).join(", ");
                    if (!confirm(`MARKET-SELL every open position whose base asset is in:\n\n${syms}\n\nThis is irreversible.`)) return;
                    try {
                      const r = await sellAffected(e.id).unwrap();
                      toast.success(`Closed ${r.closed.length}, failed ${r.failed.length}`);
                    } catch (err: any) {
                      toast.error(err?.data?.detail ?? "Sell-affected failed");
                    }
                  }}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function symbolsOf(e: NewsEvent): string[] {
  try {
    const v = JSON.parse(e.symbols_json || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function severityTone(sev: NewsEvent["severity"]): "good" | "warn" | "bad" | "muted" {
  if (sev === "critical") return "bad";
  if (sev === "high") return "bad";
  if (sev === "medium") return "warn";
  return "muted";
}

function EventCard({ event, onDismiss, onSell, selling }: {
  event: NewsEvent;
  onDismiss: () => Promise<void>;
  onSell: () => Promise<void>;
  selling: boolean;
}) {
  const syms = symbolsOf(event);
  let actions: any = null;
  try { actions = JSON.parse(event.actions_taken || "{}"); } catch { actions = {}; }
  return (
    <div className="border border-border rounded-md p-3 bg-bg2/30">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <Badge tone={severityTone(event.severity)}>{event.severity}</Badge>
        <Badge tone="info">{event.source}</Badge>
        <Badge tone="muted">{event.category}</Badge>
        {syms.map((s) => <Badge key={s} tone="warn">{s}</Badge>)}
        <span className="text-xs text-muted ml-auto">
          {event.published_at ? `${fmtRelative(event.published_at)} · ` : ""}
          detected {fmtRelative(event.detected_at)}
        </span>
      </div>
      <div className="text-sm font-medium mb-1">
        {event.title}
      </div>
      {event.summary && (
        <p className="text-xs text-muted whitespace-pre-wrap mb-2 max-h-32 overflow-auto">
          {event.summary}
        </p>
      )}
      {(actions?.paused?.length || actions?.emailed) && (
        <div className="text-xs text-muted mb-2">
          {actions.paused?.length > 0 && (
            <span>auto-paused: {actions.paused.join(", ")} · </span>
          )}
          {actions.emailed && <span>email sent · </span>}
        </div>
      )}
      <div className="flex gap-2 items-center flex-wrap">
        {event.url && (
          <a href={event.url} target="_blank" rel="noreferrer"
              className="text-xs text-accent hover:underline inline-flex items-center gap-1">
            source <ExternalLink className="w-3 h-3" />
          </a>
        )}
        <span className="text-xs text-muted ml-auto">
          {event.status === "open" ? "" : `status: ${event.status} · `}
          {fmtTime(event.detected_at)}
        </span>
        {event.status === "open" && (
          <>
            {syms.length > 0 && (event.severity === "high" || event.severity === "critical") && (
              <Button size="sm" variant="ghost" disabled={selling} onClick={onSell}
                      title="Market-sell every open position with this base asset">
                <ShieldAlert className="w-3.5 h-3.5" /> Sell affected
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onDismiss}>
              <X className="w-3.5 h-3.5" /> Dismiss
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
